import React, { useMemo, useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import AppTableContainer from "@crema/core/AppTableContainer";
import { PlanData } from "types/models/planMaster/planMasterApp";
import TableHeading from "./TableHeading";
import TableItem from "./TableItem";

type Order = "asc" | "desc";

interface PlanTableProps {
  plan: PlanData[];
  selectedPlanIds: string[]; 
  onSelectionChange?: (selectedIds: string[]) => void;
}
const PlanTable: React.FC<PlanTableProps> = ({ plan,selectedPlanIds, onSelectionChange }) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof PlanData>("planName");
  
  const handleSortRequest = (property: keyof PlanData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedPlanIds);
    }
  }, [selectedPlanIds, onSelectionChange]);

  const sortedPlans = useMemo(() => {
    return [...plan].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return order === "asc" ? -1 : 1;
      if (bVal == null) return order === "asc" ? 1 : -1;

      const aNum = parseFloat(String(aVal));
      const bNum = parseFloat(String(bVal));
      const bothAreNumbers = !isNaN(aNum) && !isNaN(bNum);

      if (bothAreNumbers) {
        return (aNum - bNum) * (order === "asc" ? 1 : -1);
      }

      return String(aVal).localeCompare(String(bVal)) * (order === "asc" ? 1 : -1);
    });
  }, [plan, order, orderBy]);

  const handleSelect = (planId: string) => {
  if (!onSelectionChange) return;

  if (selectedPlanIds.includes(planId)) {
    onSelectionChange(selectedPlanIds.filter((id) => id !== planId));
  } else if (selectedPlanIds.length < 4) {
    onSelectionChange([...selectedPlanIds, planId]);
  }
};
  return (
    <AppTableContainer>
      <Table stickyHeader className="table">
        <TableHead>
          <TableHeading order={order} orderBy={orderBy} onSort={handleSortRequest} />
        </TableHead>
        <TableBody>
          {sortedPlans.map((data) => (
            <TableItem
              key={data.planId}
              data={data}
              selected={selectedPlanIds.includes(data.planId.toString())}
              onSelect={handleSelect}
            />
          ))}
        </TableBody>
      </Table>
    </AppTableContainer>
  );
};

export default PlanTable;
